import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MagnifyingGlass, ChartLine, Target, Rocket } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const SeoHero = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const blobRef = useRef(null);
  const searchRef = useRef(null);
  const iconsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the blob shape
      gsap.fromTo(blobRef.current, 
        { 
          scale: 0.8, 
          rotation: -5,
          borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%'
        },
        { 
          scale: 1, 
          rotation: 0,
          borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
          duration: 2,
          ease: "power2.out"
        }
      );

      // Animate the search visualization
      gsap.fromTo(searchRef.current,
        { 
          y: 50, 
          opacity: 0,
          rotationY: -15
        },
        { 
          y: 0, 
          opacity: 1,
          rotationY: 0,
          duration: 1.5,
          delay: 0.5,
          ease: "back.out(1.7)"
        }
      );

      // Animate floating icons
      gsap.to(iconsRef.current.children, {
        y: -20,
        duration: 2,
        ease: "power2.inOut",
        stagger: 0.2,
        yoyo: true,
        repeat: -1
      });

      // Continuous blob morphing
      gsap.to(blobRef.current, {
        borderRadius: '70% 30% 50% 50% / 30% 50% 70% 70%',
        duration: 4,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={heroRef} className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Main Hero Content */}
      <div className="container mx-auto px-4 pt-20 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              SEO optimalisatie{' '}
              <span className="text-blue-400">die werkt</span>
            </h1>
            <p className="text-blue-400 text-xl font-medium mb-4">
              Verbeter je Google ranking en trek meer klanten aan
            </p>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-2xl">
              Van keyword onderzoek tot technische optimalisatie. We maken je website 
              vindbaar voor de juiste zoekwoorden en zorgen voor continue groei in organisch verkeer.
            </p>
            
            {/* CTA Buttons */}
            <div className="space-y-4">
              <div>
                <Link to="/start-your-project" className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg group transition-all duration-300 transform hover:scale-105">
                  GRATIS SEO AUDIT
                  <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
              <div>
                <Link to="/contact" className="inline-flex items-center text-gray-300 hover:text-blue-400 transition-colors cursor-pointer group">
                  <span className="mr-2">Liever eerst sparren?</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
            </div>

            {/* Feature Icons */}
            <div ref={iconsRef} className="flex items-center space-x-8 mt-12">
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <MagnifyingGlass className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Keyword Research</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <ChartLine className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Ranking Groei</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Target className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Traffic Boost</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Rocket className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Conversie</span>
              </div>
            </div>
          </motion.div>

          {/* Right Visual Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative flex justify-center items-center hidden lg:flex"
          >
            {/* Animated Blob Container */}
            <div 
              ref={blobRef}
              className="relative w-[500px] h-[600px] bg-gradient-to-br from-blue-500/20 via-blue-500/20 to-blue-500/20 backdrop-blur-sm rounded-[30%_60%_70%_40%_/_50%_60%_30%_60%] flex items-center justify-center"
              style={{
                boxShadow: '0 0 100px rgba(59, 130, 246, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Google Search Results Mockup */}
              <div 
                ref={searchRef}
                className="relative w-96 h-80 bg-white rounded-lg shadow-2xl overflow-hidden"
                style={{
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Google Search Header */}
                <div className="bg-white p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-6 h-6 bg-blue-500 rounded"></div>
                    <div className="text-lg font-semibold text-gray-800">Google</div>
                  </div>
                  <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                    <div className="w-4 h-4 bg-gray-400 rounded mr-3"></div>
                    <div className="flex-1 text-gray-600 text-sm">website laten maken</div>
                    <div className="w-4 h-4 bg-blue-500 rounded ml-3"></div>
                  </div>
                </div>

                {/* Search Results */}
                <div className="p-4 space-y-4 bg-white">
                  {/* First Result - Highlighted */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-blue-600 text-sm mb-1">juliux.nl</div>
                    <div className="text-blue-700 text-lg font-medium mb-1">Professionele Website Laten Maken | Juliux</div>
                    <div className="text-gray-600 text-sm">Modern, snel en gebruiksvriendelijk. We maken websites die echt resultaat opleveren voor je bedrijf...</div>
                    <div className="flex items-center mt-2 space-x-2">
                      <div className="bg-green-500 w-2 h-2 rounded-full"></div>
                      <div className="text-green-600 text-xs font-medium">#1 Positie</div>
                    </div>
                  </div>

                  {/* Other Results */}
                  <div className="space-y-3">
                    <div>
                      <div className="text-gray-500 text-sm">concurrent.nl</div>
                      <div className="text-blue-600 text-base">Website Maken - Vanaf €299</div>
                      <div className="text-gray-600 text-sm">Goedkope websites vanaf €299. Snel geleverd...</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm">anderconcurrent.nl</div>
                      <div className="text-blue-600 text-base">Webdesign Bureau - Professioneel</div>
                      <div className="text-gray-600 text-sm">Creatieve websites voor bedrijven...</div>
                    </div>
                  </div>
                </div>

                {/* SEO Stats overlay */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600 mb-1">SEO Score</div>
                  <div className="text-lg font-bold text-green-600">98/100</div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-500/30 rounded-full blur-sm"></div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-blue-500/30 rounded-full blur-sm"></div>
              <div className="absolute top-1/2 -right-8 w-12 h-12 bg-blue-500/30 rounded-full blur-sm"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SeoHero;
